import { sampleForPlayback, sampleNearest } from "./poseTimeline";

function jointNorm(j) {
  if (!j) return null;
  const x = Number(j.x);
  const y = Number(j.y);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  if ((j.score ?? 1) < 0.12) return null;
  return { x, y };
}

function angleDeg(a, b, c) {
  const ab = { x: a.x - b.x, y: a.y - b.y };
  const cb = { x: c.x - b.x, y: c.y - b.y };
  const dot = ab.x * cb.x + ab.y * cb.y;
  const mag =
    Math.hypot(ab.x, ab.y) * Math.hypot(cb.x, cb.y) || 1e-6;
  const cos = Math.min(1, Math.max(-1, dot / mag));
  return Math.round((Math.acos(cos) * 180) / Math.PI);
}

function kneeTriples(joints) {
  const isMp = Array.isArray(joints) && joints.length >= 33;
  if (isMp) {
    return [
      { hip: 23, knee: 25, ankle: 27, label: "L" },
      { hip: 24, knee: 26, ankle: 28, label: "R" },
    ];
  }
  return [
    { hip: 11, knee: 13, ankle: 15, label: "L" },
    { hip: 12, knee: 14, ankle: 16, label: "R" },
  ];
}

function storedKneeAngles(side) {
  const a = side?.angles;
  if (!a || typeof a !== "object") return { left: null, right: null };
  const left = Number(a.left_knee);
  const right = Number(a.right_knee);
  return {
    left: Number.isFinite(left) ? Math.round(left) : null,
    right: Number.isFinite(right) ? Math.round(right) : null,
  };
}

function computedKneeAngles(side) {
  const joints =
    side?.joints_full?.length >= 33 ? side.joints_full : side?.joints;
  if (!Array.isArray(joints)) return { left: null, right: null };
  const out = { left: null, right: null };
  for (const triple of kneeTriples(joints)) {
    const hip = jointNorm(joints[triple.hip]);
    const knee = jointNorm(joints[triple.knee]);
    const ankle = jointNorm(joints[triple.ankle]);
    if (!hip || !knee || !ankle) continue;
    const deg = angleDeg(hip, knee, ankle);
    if (triple.label === "L") out.left = deg;
    else out.right = deg;
  }
  return out;
}

function kneeAnglesForSide(side) {
  const stored = storedKneeAngles(side);
  const computed = computedKneeAngles(side);
  return {
    left: stored.left ?? computed.left,
    right: stored.right ?? computed.right,
  };
}

export { kneeAnglesForSide };

function elbowTriples(joints) {
  const isMp = Array.isArray(joints) && joints.length >= 33;
  if (isMp) {
    return [
      { shoulder: 11, elbow: 13, wrist: 15, label: "L" },
      { shoulder: 12, elbow: 14, wrist: 16, label: "R" },
    ];
  }
  return [
    { shoulder: 5, elbow: 7, wrist: 9, label: "L" },
    { shoulder: 6, elbow: 8, wrist: 10, label: "R" },
  ];
}

function storedElbowAngles(side) {
  const a = side?.angles;
  if (!a || typeof a !== "object") return { left: null, right: null };
  const left = Number(a.left_elbow);
  const right = Number(a.right_elbow);
  return {
    left: Number.isFinite(left) ? Math.round(left) : null,
    right: Number.isFinite(right) ? Math.round(right) : null,
  };
}

function computedElbowAngles(side) {
  const joints =
    side?.joints_full?.length >= 33 ? side.joints_full : side?.joints;
  if (!Array.isArray(joints)) return { left: null, right: null };
  const out = { left: null, right: null };
  for (const triple of elbowTriples(joints)) {
    const shoulder = jointNorm(joints[triple.shoulder]);
    const elbow = jointNorm(joints[triple.elbow]);
    const wrist = jointNorm(joints[triple.wrist]);
    if (!shoulder || !elbow || !wrist) continue;
    const deg = angleDeg(shoulder, elbow, wrist);
    if (triple.label === "L") out.left = deg;
    else out.right = deg;
  }
  return out;
}

function elbowAnglesForSide(side) {
  const stored = storedElbowAngles(side);
  const computed = computedElbowAngles(side);
  return {
    left: stored.left ?? computed.left,
    right: stored.right ?? computed.right,
  };
}

export { elbowAnglesForSide };

function drawKneeArc(ctx, hip, knee, ankle, color, highlight) {
  const toHip = { x: hip.x - knee.x, y: hip.y - knee.y };
  const toAnkle = { x: ankle.x - knee.x, y: ankle.y - knee.y };
  const thighLen = Math.hypot(toHip.x, toHip.y);
  const shinLen = Math.hypot(toAnkle.x, toAnkle.y);
  const radius = Math.min(
    Math.max(14, Math.min(ctx.canvas.width, ctx.canvas.height) / 28),
    thighLen * 0.35,
    shinLen * 0.35
  );
  if (!(radius > 5)) return;

  const start = Math.atan2(toHip.y, toHip.x);
  const end = Math.atan2(toAnkle.y, toAnkle.x);
  let sweep = end - start;
  while (sweep <= -Math.PI) sweep += Math.PI * 2;
  while (sweep > Math.PI) sweep -= Math.PI * 2;
  if (Math.abs(sweep) < 0.05) return;

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(knee.x, knee.y);
  ctx.arc(knee.x, knee.y, radius, start, start + sweep, sweep < 0);
  ctx.closePath();
  ctx.fillStyle = highlight ? `${color}55` : `${color}33`;
  ctx.fill();
  ctx.lineWidth = highlight ? 4 : 3;
  ctx.shadowColor = color;
  ctx.shadowBlur = highlight ? 16 : 10;
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.shadowBlur = 0;

  ctx.beginPath();
  ctx.arc(knee.x, knee.y, radius, start, start + sweep, sweep < 0);
  ctx.lineWidth = highlight ? 2 : 1.5;
  ctx.strokeStyle = highlight ? "#FFF59D" : "#FFEE58";
  ctx.stroke();
  ctx.restore();
}

/**
 * Draw knee angle overlays on a canvas that already contains the exported frame.
 * Joint coordinates are normalized to the full source video; cropRect or videoRect
 * maps them into canvas space.
 * @param {CanvasRenderingContext2D} ctx
 * @param {{ joints?: Array, joints_full?: Array }} sideSample
 * @param {{ left?: number|null, right?: number|null, minLeft?: number|null, minRight?: number|null }} kneeState
 * @param {{ x: number, y: number, w: number, h: number }} [cropRect]
 * @param {{ x: number, y: number, w: number, h: number }} [videoRect]
 */
export function drawKneeOverlayOnCanvas(
  ctx,
  sideSample,
  kneeState = {},
  cropRect = null,
  videoRect = null,
  color = "#FFD54F",
  options = {}
) {
  const emphasize = Boolean(options.emphasize);
  const player = sideSample?.left || sideSample?.right || sideSample;
  const joints =
    player?.joints_full?.length >= 33 ? player.joints_full : player?.joints;
  if (!Array.isArray(joints)) return;

  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  const computed = kneeAnglesForSide(player);
  const leftDeg = kneeState.left ?? computed.left;
  const rightDeg = kneeState.right ?? computed.right;
  const mapPoint = (p) => {
    if (videoRect && cropRect) {
      return {
        x: videoRect.x + ((p.x - cropRect.x) / cropRect.w) * videoRect.w,
        y: videoRect.y + ((p.y - cropRect.y) / cropRect.h) * videoRect.h,
      };
    }
    if (videoRect) {
      return {
        x: videoRect.x + p.x * videoRect.w,
        y: videoRect.y + p.y * videoRect.h,
      };
    }
    if (!cropRect) return { x: p.x * w, y: p.y * h };
    return {
      x: ((p.x - cropRect.x) / cropRect.w) * w,
      y: ((p.y - cropRect.y) / cropRect.h) * h,
    };
  };

  for (const triple of kneeTriples(joints)) {
    const hip = jointNorm(joints[triple.hip]);
    const knee = jointNorm(joints[triple.knee]);
    const ankle = jointNorm(joints[triple.ankle]);
    if (!hip || !knee || !ankle) continue;
    const px = mapPoint;
    const deg =
      triple.label === "L" ? leftDeg : rightDeg;
    const minDeg =
      triple.label === "L" ? kneeState.minLeft : kneeState.minRight;
    const highlight =
      emphasize ||
      (deg != null && minDeg != null && Math.abs(deg - minDeg) <= 2);
    drawKneeArc(ctx, px(hip), px(knee), px(ankle), color, highlight);

    if (deg != null) {
      ctx.save();
      const kneePt = px(knee);
      const label = `${deg}°`;
      const gap = Math.max(10, Math.round(w * 0.018));
      ctx.font = `700 ${Math.max(14, Math.round(w * 0.04))}px sans-serif`;
      ctx.textAlign = triple.label === "L" ? "right" : "left";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#FFFFFF";
      ctx.strokeStyle = "rgba(0,0,0,0.65)";
      ctx.lineWidth = 3;
      const tx = triple.label === "L" ? kneePt.x - gap : kneePt.x + gap;
      const ty = kneePt.y;
      ctx.strokeText(label, tx, ty);
      ctx.fillText(label, tx, ty);
      ctx.restore();
    }
  }
}

/**
 * Draw elbow angle overlays (shoulder–elbow–wrist) on a canvas.
 */
export function drawElbowOverlayOnCanvas(
  ctx,
  sideSample,
  elbowState = {},
  cropRect = null,
  videoRect = null,
  color = "#FFD54F",
  options = {}
) {
  const emphasize = Boolean(options.emphasize);
  const player = sideSample?.left || sideSample?.right || sideSample;
  const joints =
    player?.joints_full?.length >= 33 ? player.joints_full : player?.joints;
  if (!Array.isArray(joints)) return;

  const w = ctx.canvas.width;
  const h = ctx.canvas.height;
  const angles = {
    left: elbowState.left ?? elbowAnglesForSide(player).left,
    right: elbowState.right ?? elbowAnglesForSide(player).right,
  };
  const mapPoint = (p) => {
    if (videoRect && cropRect) {
      return {
        x: videoRect.x + ((p.x - cropRect.x) / cropRect.w) * videoRect.w,
        y: videoRect.y + ((p.y - cropRect.y) / cropRect.h) * videoRect.h,
      };
    }
    if (videoRect) {
      return {
        x: videoRect.x + p.x * videoRect.w,
        y: videoRect.y + p.y * videoRect.h,
      };
    }
    if (!cropRect) return { x: p.x * w, y: p.y * h };
    return {
      x: ((p.x - cropRect.x) / cropRect.w) * w,
      y: ((p.y - cropRect.y) / cropRect.h) * h,
    };
  };

  for (const triple of elbowTriples(joints)) {
    const shoulder = jointNorm(joints[triple.shoulder]);
    const elbow = jointNorm(joints[triple.elbow]);
    const wrist = jointNorm(joints[triple.wrist]);
    if (!shoulder || !elbow || !wrist) continue;
    const px = mapPoint;
    const deg = triple.label === "L" ? angles.left : angles.right;
    drawKneeArc(ctx, px(shoulder), px(elbow), px(wrist), color, emphasize);

    if (deg != null) {
      ctx.save();
      const elbowPt = px(elbow);
      const label = `${deg}°`;
      const gap = Math.max(10, Math.round(w * 0.018));
      ctx.font = `700 ${Math.max(14, Math.round(w * 0.04))}px sans-serif`;
      ctx.textAlign = triple.label === "L" ? "right" : "left";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#FFFFFF";
      ctx.strokeStyle = "rgba(0,0,0,0.65)";
      ctx.lineWidth = 3;
      const tx = triple.label === "L" ? elbowPt.x - gap : elbowPt.x + gap;
      const ty = elbowPt.y;
      ctx.strokeText(label, tx, ty);
      ctx.fillText(label, tx, ty);
      ctx.restore();
    }
  }
}

export function poseSideHasJoints(side) {
  if (!side || typeof side !== "object") return false;
  return (
    (Array.isArray(side.joints_full) && side.joints_full.length >= 13) ||
    (Array.isArray(side.joints) && side.joints.length >= 13)
  );
}

/** Left and/or right player, or null when neither side has a drawable pose. */
export function jointOverlaySampleFromSides(left, right) {
  const sample = {};
  if (poseSideHasJoints(left)) sample.left = left;
  if (poseSideHasJoints(right)) sample.right = right;
  return sample.left || sample.right ? sample : null;
}

/**
 * Pose returned by calibrate-frame.
 * Eval defaults to Player A (suggested_left). Coach can pick B (right).
 * Table replay uses both halves, then any near-table candidate — a phone
 * camera often puts the only player on the right, or facing screen-left,
 * so suggested_left is empty.
 *
 * @param {object} data calibrate-frame payload
 * @param {{ both?: boolean, side?: "left"|"right" }} [opts]
 */
export function jointOverlaySampleFromCalibration(
  data,
  { both = false, side = "left" } = {}
) {
  if (!data || typeof data !== "object") return null;
  const wantRight = side === "right";
  const split = both
    ? jointOverlaySampleFromSides(data.suggested_left, data.suggested_right)
    : wantRight
      ? jointOverlaySampleFromSides(null, data.suggested_right)
      : jointOverlaySampleFromSides(data.suggested_left, null);
  if (split) return split;
  if (!both) {
    // Single-ROI validate returns { player } instead of suggested_left/right.
    if (poseSideHasJoints(data.player)) {
      return wantRight
        ? jointOverlaySampleFromSides(null, data.player)
        : jointOverlaySampleFromSides(data.player, null);
    }
    return null;
  }
  const candidates = Array.isArray(data.candidates) ? data.candidates : [];
  for (const candidate of candidates) {
    const sample = jointOverlaySampleFromSides(candidate, null);
    if (sample) return sample;
  }
  return jointOverlaySampleFromSides(data.player, null);
}

/** Build an overlay sample from a single-ROI calibrate-frame response. */
export function jointOverlaySampleFromRoiDetection(data, side = "left") {
  if (!data?.found || !poseSideHasJoints(data.player)) return null;
  return side === "right"
    ? jointOverlaySampleFromSides(null, data.player)
    : jointOverlaySampleFromSides(data.player, null);
}

export function poseSampleHasJoints(sample) {
  if (!sample || typeof sample !== "object") return false;
  return (
    poseSideHasJoints(sample.left) ||
    poseSideHasJoints(sample.right) ||
    poseSideHasJoints(sample)
  );
}

export function buildKneeOverlayState(samples, poseSide, sourceStartSec, sourceEndSec) {
  let minLeft = null;
  let minRight = null;
  const startMs = Math.round(sourceStartSec * 1000);
  const endMs = Math.round(sourceEndSec * 1000);
  for (const sample of samples || []) {
    const tMs = Number(sample?.t_ms);
    if (!Number.isFinite(tMs) || tMs < startMs || tMs > endMs) continue;
    const side = poseSide === "right" ? sample.right : sample.left;
    if (!side) continue;
    const angles = kneeAnglesForSide(side);
    if (angles.left != null) {
      minLeft = minLeft == null ? angles.left : Math.min(minLeft, angles.left);
    }
    if (angles.right != null) {
      minRight = minRight == null ? angles.right : Math.min(minRight, angles.right);
    }
  }
  return { minLeft, minRight };
}

function lerpNum(a, b, u) {
  if (a == null || !Number.isFinite(Number(a))) return b;
  if (b == null || !Number.isFinite(Number(b))) return a;
  return Number(a) + (Number(b) - Number(a)) * u;
}

function lerpJoints(a, b, u) {
  if (!Array.isArray(a)) return b;
  if (!Array.isArray(b) || !b.length) return a;
  const n = Math.min(a.length, b.length);
  const out = a.slice();
  for (let i = 0; i < n; i += 1) {
    const ja = a[i];
    const jb = b[i];
    if (!ja) {
      out[i] = jb;
      continue;
    }
    if (!jb) continue;
    out[i] = {
      x: lerpNum(ja.x, jb.x, u),
      y: lerpNum(ja.y, jb.y, u),
      score: Math.min(ja.score ?? 1, jb.score ?? 1),
    };
  }
  return out;
}

function interpolatedSideAtTime(samples, poseSide, tMs) {
  if (!Array.isArray(samples) || !samples.length || !Number.isFinite(tMs)) {
    return null;
  }
  let i = 0;
  while (i + 1 < samples.length && Number(samples[i + 1]?.t_ms) <= tMs) {
    i += 1;
  }
  const a = samples[i];
  const b = samples[Math.min(i + 1, samples.length - 1)];
  const ta = Number(a?.t_ms);
  const tb = Number(b?.t_ms);
  const u =
    Number.isFinite(ta) && Number.isFinite(tb) && tb > ta
      ? Math.min(1, Math.max(0, (tMs - ta) / (tb - ta)))
      : 0;
  const sideA = poseSide === "right" ? a?.right : a?.left;
  const sideB = poseSide === "right" ? b?.right : b?.left;
  if (!sideA) return sideB || null;
  if (!sideB || u <= 0.001) return sideA;
  if (u >= 0.999) return sideB;
  const useFull =
    sideA.joints_full?.length >= 33 || sideB.joints_full?.length >= 33;
  const anglesA = kneeAnglesForSide(sideA);
  const anglesB = kneeAnglesForSide(sideB);
  return {
    ...sideA,
    joints: useFull ? sideA.joints : lerpJoints(sideA.joints, sideB.joints, u),
    joints_full: useFull
      ? lerpJoints(sideA.joints_full, sideB.joints_full, u)
      : sideA.joints_full,
    angles: {
      ...(sideA.angles || {}),
      left_knee: Math.round(lerpNum(anglesA.left, anglesB.left, u)),
      right_knee: Math.round(lerpNum(anglesA.right, anglesB.right, u)),
    },
  };
}

export function kneeStateAtTime(samples, poseSide, sourceSec, minState) {
  const tMs = Number(sourceSec) * 1000;
  const nearest = Number.isFinite(tMs)
    ? sampleForPlayback(samples, { tMs, padMs: 400 }) ||
      sampleNearest(samples, tMs, 800)
    : null;
  const side =
    interpolatedSideAtTime(samples, poseSide, tMs) ||
    (poseSide === "right" ? nearest?.right : nearest?.left) ||
    null;
  if (!side) {
    return {
      sample: null,
      angles: { left: null, right: null },
      minLeft: minState?.minLeft ?? null,
      minRight: minState?.minRight ?? null,
      left: null,
      right: null,
    };
  }
  const angles = kneeAnglesForSide(side);
  return {
    sample: side,
    angles,
    minLeft: minState?.minLeft ?? null,
    minRight: minState?.minRight ?? null,
    left: angles.left,
    right: angles.right,
  };
}
